import { addDays, ExerciseList, OkResponse, SplitList, type Split, type User } from '@squats/shared';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { api, errorText } from '../api.ts';
import { useCachedApi } from '../cache.ts';
import { dayOf, forUser, PlanBadge, type Calendar } from '../calendar.tsx';
import { useApi } from '../useApi.ts';
import { PlanEditor, plannedToEntries, PlannedList } from './Plan.tsx';
import { formatDayLong, formatDayShort } from '../dates.ts';
import { Sheet } from './Sheet.tsx';

const Anything = z.unknown();

/**
 * Change one person's day (the planner's sheet, for anyone): what to train, its exercises, swap, skip/push forward,
 * who they're training with.
 */
export function DaySheet({
  open,
  onClose,
  onChanged,
  cal,
  editor,
  subjectId,
  date,
  today,
}: {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  cal: Calendar;
  /** Who's making the change (the planner). */
  editor: User;
  /** Whose day it is. */
  subjectId: string;
  date: string;
  today: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [swapWith, setSwapWith] = useState(addDays(date, 1));
  const [editingExercises, setEditingExercises] = useState(false);
  const path = (p: string) => forUser(p, subjectId, editor);
  const { data: splitList } = useApi(() => api(path('/api/splits'), SplitList), [subjectId]);
  const splits: Split[] = splitList?.splits ?? [];
  const { data: library } = useCachedApi(editor.id, '/api/exercises', ExerciseList);
  const exercises = useMemo(() => new Map((library?.exercises ?? []).map((e) => [e.id, e])), [library]);
  const day = dayOf(cal, subjectId, date);
  const subject = cal.users.find((u) => u.id === subjectId);
  const units = subject?.units ?? editor.units;
  const others = cal.users.filter((u) => u.id !== subjectId);
  const split = day?.splitId ? splits.find((s) => s.id === day.splitId) : undefined;

  async function run(action: () => Promise<unknown>, close = true) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
      if (close) onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const setDay = (body: object) => run(() => api(path(`/api/calendar/${date}`), Anything, { method: 'PUT', body }));
  const title = `${subjectId === editor.id ? '' : `${subject?.displayName ?? 'Someone'} · `}${formatDayLong(date)}`;

  if (day && editingExercises) {
    return (
      <Sheet open={open} onClose={onClose} title={`${title}: exercises`}>
        <PlanEditor
          mode="day"
          initial={plannedToEntries(day.exercises, units)}
          exercises={exercises}
          groups={split?.muscleGroups ?? []}
          units={units}
          onCancel={() => setEditingExercises(false)}
          onSave={async (entries) => {
            await api(path(`/api/calendar/${date}/exercises`), OkResponse, { method: 'PUT', body: entries });
            onChanged();
            setEditingExercises(false);
          }}
        />
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onClose={onClose} title={title}>
      {day && (
        <div className="stack">
          <div className="row wrap">
            <PlanBadge cal={cal} day={day} />
            {day.planChanged && (
              <>
                <span className="muted small">Changed for this day</span>
                <button type="button" className="btn small" disabled={busy} onClick={() => run(() => api(path(`/api/calendar/${date}`), Anything, { method: 'DELETE' }))}>
                  Back to schedule
                </button>
              </>
            )}
          </div>

          {day.kind === 'split' && (
            <section className="stack" aria-label="Exercises">
              <h3>Exercises</h3>
              {day.exercises.length ? (
                <PlannedList planned={day.exercises} exercises={exercises} units={units} />
              ) : (
                <p className="muted small">Nothing planned: add exercises to the split’s template in Splits & schedule, or just for this day.</p>
              )}
              <p className="muted small">{day.exercisesEdited ? 'Changed for this day only.' : 'From the split’s template.'}</p>
              <div className="row wrap">
                <button type="button" className="btn" disabled={busy} onClick={() => setEditingExercises(true)}>
                  Edit this day’s exercises
                </button>
                {day.exercisesEdited && (
                  <button type="button" className="btn" disabled={busy} onClick={() => run(() => api(path(`/api/calendar/${date}/exercises`), OkResponse, { method: 'DELETE' }), false)}>
                    Back to the template
                  </button>
                )}
              </div>
            </section>
          )}

          <section className="stack" aria-label="Train">
            <h3>Train</h3>
            <div className="choice-grid">
              {splits.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="btn"
                  aria-pressed={day.kind === 'split' && day.splitId === s.id}
                  disabled={busy}
                  onClick={() => setDay({ kind: 'split', splitId: s.id })}
                >
                  {s.name}
                </button>
              ))}
              <button type="button" className="btn" aria-pressed={day.kind === 'rest'} disabled={busy} onClick={() => setDay({ kind: 'rest' })}>
                Rest
              </button>
              <button type="button" className="btn" aria-pressed={day.kind === 'skip'} disabled={busy} onClick={() => setDay({ kind: 'skip' })}>
                Skip
              </button>
            </div>
            {splits.length === 0 && <p className="muted small">Add splits in Plan → Splits & schedule.</p>}
          </section>

          {day.kind === 'split' && date <= today && day.source !== 'none' && (
            <section className="stack" aria-label="Missed it">
              <h3>Missed it?</h3>
              <p className="muted small">Marks this day skipped and moves the schedule back a day, so the next day picks up where it left off.</p>
              <div>
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => run(() => api(path('/api/calendar/push-forward'), OkResponse, { body: { missedDate: date } }))}
                >
                  Push schedule forward
                </button>
              </div>
            </section>
          )}

          <section className="stack" aria-label="Swap">
            <h3>Swap with another day</h3>
            <div className="row">
              <input type="date" aria-label="Day to swap with" value={swapWith} onChange={(e) => setSwapWith(e.target.value)} />
              <button
                type="button"
                className="btn"
                disabled={busy || !swapWith || swapWith === date}
                onClick={() => run(() => api(path('/api/calendar/swap'), Anything, { body: { a: date, b: swapWith } }))}
              >
                Swap
              </button>
            </div>
            {swapWith && swapWith !== date && <p className="muted small">Trades plans with {formatDayShort(swapWith)}.</p>}
          </section>

          {others.length > 0 && (
            <section className="stack" aria-label="Training together">
              <h3>Training together</h3>
              {others.map((u) => {
                const on = day.together.includes(u.id);
                return (
                  <label key={u.id} className="check">
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={busy}
                      onChange={() =>
                        run(
                          () =>
                            on
                              ? api(path(`/api/calendar/${date}/together`), OkResponse, { method: 'DELETE' })
                              : api(path(`/api/calendar/${date}/together`), OkResponse, { method: 'PUT', body: { withUserId: u.id } }),
                          false,
                        )
                      }
                    />
                    <span>{u.displayName}</span>
                  </label>
                );
              })}
              {day.together.length > 1 && <p className="muted small">Unticking takes {subjectId === editor.id ? 'you' : subject?.displayName} out of the group; the others stay together.</p>}
            </section>
          )}

          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </Sheet>
  );
}
